import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  FileSearch, FileUp, Loader2, Search, Trash2, Download, FileText, AlertTriangle, Copy,
} from "lucide-react";
import aapi, { formatApiError, inr, fmtDate } from "./aapi";
import { PageHeader, inputCls, EmptyState } from "@/panel/components";
import { EASE } from "@/components/Motion";

export default function AdminAnalyzer() {
  const [docs, setDocs] = useState([]);
  const [account, setAccount] = useState("");
  const [pdfPassword, setPdfPassword] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);

  const loadDocs = useCallback(async () => {
    try {
      const { data } = await aapi.get("/analyzer/documents");
      setDocs(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const upload = async () => {
    if (!files.length) return toast.error("Pehle statement files choose karein");
    setUploading(true);
    setUploadResults(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (account.trim()) fd.append("account", account.trim());
      if (pdfPassword.trim()) fd.append("password", pdfPassword.trim());
      const { data } = await aapi.post("/analyzer/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      setUploadResults(data.results);
      const okCount = data.results.filter((r) => r.ok).length;
      if (okCount) toast.success(`${okCount} file(s) import ho gayi — 100% local parsing, zero credits`);
      if (okCount < data.results.length) toast.warning("Kuch files import nahi ho payi — neeche reason dekhein");
      setFiles([]);
      await loadDocs();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async (d) => {
    if (!window.confirm(`"${d.filename}" aur iski ${d.txn_count} transactions delete karein?`)) return;
    try {
      await aapi.delete(`/analyzer/documents/${d.id}`);
      toast.success("Document deleted");
      await loadDocs();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const search = async (q = query) => {
    setSearching(true);
    try {
      const { data } = await aapi.get("/analyzer/search", { params: { q } });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSearching(false);
    }
  };

  const exportCsv = () => {
    if (!result?.transactions?.length) return toast.error("Pehle search karein");
    const head = ["Date", "Time", "Payer", "Description", "UTR/Ref", "Debit", "Credit", "Account", "Source File", "Flags"];
    const lines = [head.join(",")];
    for (const r of result.transactions) {
      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      lines.push([
        r.date || "", r.time || "", esc(r.payer), esc(r.description), r.utr || "",
        r.type === "debit" ? r.amount : "", r.type === "credit" ? r.amount : "",
        esc(r.account), esc(r.source_file),
        [r.duplicate && "DUPLICATE", r.unclear && "UNCLEAR"].filter(Boolean).join("+"),
      ].join(","));
    }
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `statement-analysis-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("CSV download ho gayi");
  };

  const exportPdf = async () => {
    try {
      const { data } = await aapi.get("/analyzer/export.pdf", { params: { q: query }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      a.download = `statement-analysis-${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("PDF download ho gayi");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const t = result?.totals;

  return (
    <div data-testid="admin-analyzer-page">
      <PageHeader
        title="Statement Analyzer"
        sub="Apne total bank statements (PDF/CSV/Excel) upload karein aur plain language mein search karein — 'Jitendra Soni ke payments dikhao'. Sab kuch 100% local parsing se — zero AI credits. Sirf admin ke liye private."
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* upload */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
            className="rounded-3xl border border-gold/20 bg-panel p-7" data-testid="analyzer-upload-card">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10">
                <FileUp size={18} className="text-gold" />
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">UPLOAD STATEMENTS</div>
                <p className="mt-1 text-xs text-neutral-500">Ek saath multiple files — PDF, CSV ya Excel (max 10 files, 10MB each).</p>
              </div>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <input value={account} onChange={(e) => setAccount(e.target.value)} maxLength={60}
                placeholder="Account label (optional) — e.g. HDFC ****7821"
                className={inputCls} data-testid="analyzer-account-input" />
              <input type="password" value={pdfPassword} onChange={(e) => setPdfPassword(e.target.value)} maxLength={60}
                placeholder="PDF password (agar statement locked hai)"
                className={inputCls} data-testid="analyzer-password-input" />
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-gold/40 bg-gold/[0.05] px-4 py-3 text-xs font-medium text-goldlight transition-colors hover:border-gold/70"
                data-testid="analyzer-file-button">
                <FileUp size={14} />
                {files.length ? `${files.length} file(s) selected` : "Choose statement files"}
                <input type="file" multiple accept=".pdf,.csv,.xlsx,.xlsm" className="hidden"
                  onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="analyzer-file-input" />
              </label>
            </div>
            {files.length > 0 && (
              <p className="mt-3 font-mono text-[9px] tracking-[0.08em] text-neutral-500" data-testid="analyzer-file-list">
                {files.map((f) => f.name).join(" · ")}
              </p>
            )}
            <button onClick={upload} disabled={uploading || !files.length} data-testid="analyzer-upload-button"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3 text-xs font-bold tracking-[0.12em] text-black disabled:opacity-40">
              {uploading && <Loader2 size={14} className="animate-spin" />}
              IMPORT LOCALLY — NO AI CREDITS
            </button>
            {uploadResults && (
              <div className="mt-4 space-y-1.5" data-testid="analyzer-upload-results">
                {uploadResults.map((r, i) => (
                  <div key={i} className={`flex items-center justify-between rounded-lg border px-3 py-2 font-mono text-[10px] ${
                    r.ok ? "border-emerald-400/20 bg-emerald-400/[0.05] text-emerald-300" : "border-rose-400/20 bg-rose-400/[0.05] text-rose-300"
                  }`} data-testid={`analyzer-upload-result-${i}`}>
                    <span className="truncate">{r.file}</span>
                    <span className="ml-3 shrink-0">{r.ok ? `${r.rows} rows${r.unclear ? ` · ${r.unclear} unclear` : ""}` : r.error}</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* search */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE, delay: 0.08 }}
            className="rounded-3xl border border-gold/20 bg-panel p-7" data-testid="analyzer-search-card">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10">
                <Search size={18} className="text-gold" />
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">PLAIN LANGUAGE SEARCH</div>
                <p className="mt-1 text-xs text-neutral-500">Keywords description, payer naam aur UTR — teeno mein match hote hain.</p>
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <input value={query} onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder='e.g. "Jitendra Soni ke ab tak ke transactions nikalo" ya "ChatGPT payments"'
                className={inputCls} data-testid="analyzer-search-input" />
              <button onClick={() => search()} disabled={searching} data-testid="analyzer-search-button"
                className="flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark px-6 text-xs font-bold tracking-[0.1em] text-black disabled:opacity-40">
                {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} SEARCH
              </button>
            </div>
            {result && (
              <div className="mt-3 flex flex-wrap items-center gap-2 font-mono text-[9px] tracking-[0.12em] text-neutral-500" data-testid="analyzer-meta">
                {result.tokens.length > 0 && (
                  <span className="rounded-full border border-gold/30 bg-gold/10 px-2.5 py-1 text-goldlight">
                    KEYWORDS: {result.tokens.join(" + ").toUpperCase()}
                  </span>
                )}
                <span>{t.count} MATCHES</span>
                {t.duplicates > 0 && <span className="rounded-full border border-amber-400/30 px-2.5 py-1 text-amber-300">{t.duplicates} DUPLICATE FLAGGED</span>}
                {t.unclear > 0 && <span className="rounded-full border border-rose-400/30 px-2.5 py-1 text-rose-300">{t.unclear} UNCLEAR</span>}
                <span className="ml-auto flex gap-2">
                  <button onClick={exportCsv} data-testid="analyzer-export-csv"
                    className="flex items-center gap-1 rounded-lg border border-white/15 px-3 py-1.5 text-neutral-300 transition-colors hover:border-gold/50 hover:text-goldlight">
                    <Download size={11} /> CSV
                  </button>
                  <button onClick={exportPdf} data-testid="analyzer-export-pdf"
                    className="flex items-center gap-1 rounded-lg border border-white/15 px-3 py-1.5 text-neutral-300 transition-colors hover:border-gold/50 hover:text-goldlight">
                    <Download size={11} /> PDF
                  </button>
                </span>
              </div>
            )}

            {result && (
              <>
                <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="analyzer-totals">
                  {[["CREDITS", t.credit_total, "text-emerald-300"], ["DEBITS", t.debit_total, "text-rose-300"],
                    ["NET", t.net, t.net >= 0 ? "text-emerald-300" : "text-rose-300"], ["MATCHES", t.count, "text-goldlight"]].map(([l, v, c]) => (
                    <div key={l} className="rounded-xl border border-gold/20 bg-gold/[0.05] px-3 py-2.5">
                      <div className="font-mono text-[8px] tracking-[0.15em] text-neutral-500">{l}</div>
                      <div className={`mt-1 text-sm font-bold ${c}`}>{typeof v === "number" && l !== "MATCHES" ? inr(v) : v}</div>
                    </div>
                  ))}
                </div>

                {result.transactions.length === 0 ? (
                  <EmptyState icon={FileSearch} title="Koi match nahi mila" sub="Doosre naam/spelling se try karein, ya pehle statements upload karein" testid="analyzer-empty" />
                ) : (
                  <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10" data-testid="analyzer-table">
                    <div className="grid min-w-[860px] grid-cols-12 gap-2 border-b border-white/10 bg-white/[0.02] px-4 py-3 font-mono text-[9px] uppercase tracking-[0.15em] text-neutral-500">
                      <span className="col-span-2">DATE · TIME</span>
                      <span className="col-span-3">PAYER / DESCRIPTION</span>
                      <span className="col-span-2">UTR / REF</span>
                      <span className="col-span-2 text-right">DEBIT</span>
                      <span className="col-span-2 text-right">CREDIT</span>
                      <span className="col-span-1 text-right">SRC</span>
                    </div>
                    {result.transactions.map((r) => (
                      <div key={r.id} className="grid min-w-[860px] grid-cols-12 items-center gap-2 border-b border-white/5 px-4 py-2.5 text-xs last:border-0 hover:bg-white/[0.02]"
                        data-testid={`analyzer-row-${r.id}`}>
                        <span className="col-span-2 font-mono text-[10px] text-neutral-400">{r.date || "—"}{r.time ? ` ${r.time}` : ""}</span>
                        <span className="col-span-3 min-w-0">
                          <span className="flex items-center gap-1.5">
                            {r.duplicate && <span title="Duplicate" data-testid={`analyzer-dup-${r.id}`}><Copy size={11} className="shrink-0 text-amber-300" /></span>}
                            {r.unclear && <span title="Unclear entry" data-testid={`analyzer-unclear-${r.id}`}><AlertTriangle size={11} className="shrink-0 text-rose-300" /></span>}
                            <span className="truncate font-medium text-neutral-100">{r.payer || "—"}</span>
                          </span>
                          {r.description && <span className="block truncate font-mono text-[9px] text-neutral-600">{r.description}</span>}
                        </span>
                        <span className="col-span-2 truncate font-mono text-[10px] text-neutral-500">{r.utr || "—"}</span>
                        <span className="col-span-2 text-right font-semibold text-rose-300">{r.type === "debit" && r.amount != null ? inr(r.amount) : ""}</span>
                        <span className="col-span-2 text-right font-semibold text-emerald-300">{r.type === "credit" && r.amount != null ? inr(r.amount) : ""}</span>
                        <span className="col-span-1 truncate text-right font-mono text-[9px] text-neutral-600" title={r.account}>{(r.account || "—").slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </motion.div>
        </div>

        {/* documents */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE, delay: 0.15 }}
          className="rounded-3xl border border-white/10 bg-panel p-6" data-testid="analyzer-docs">
          <div className="flex items-center gap-3">
            <FileText size={16} className="text-gold" />
            <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">IMPORTED STATEMENTS</div>
          </div>
          <div className="mt-4 space-y-2">
            {docs.length === 0 ? (
              <p className="text-xs text-neutral-600">Abhi koi statement import nahi hui. PDF/CSV/Excel upload karein.</p>
            ) : (
              docs.map((d) => (
                <div key={d.id} className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3" data-testid={`analyzer-doc-${d.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs font-semibold text-neutral-100">{d.filename}</span>
                    <button onClick={() => removeDoc(d)} aria-label="Delete document" data-testid={`analyzer-doc-delete-${d.id}`}
                      className="shrink-0 text-neutral-600 transition-colors hover:text-rose-300">
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <p className="mt-1 font-mono text-[9px] text-neutral-600">
                    {d.account} · {d.txn_count} rows · {fmtDate(d.uploaded_at)}
                  </p>
                </div>
              ))
            )}
          </div>
          <p className="mt-4 font-mono text-[9px] leading-relaxed tracking-[0.08em] text-neutral-600">
            PRIVATE — sirf admin dekh sakta hai. Missing details kabhi invent nahi hote; unclear rows [?] flag hoti hain.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
